import type { IRowNode } from "ag-grid-community";
import type {
	BreakLine,
	BreakRule,
	ShiftHours,
} from "@/shared/models/BreakRule";
import { sortAssetsByArea } from "@/shared/utils/assets";
import type { BreakScheduleFiltersState } from "../components/BreakScheduleFilters";

type AreaIds = Parameters<typeof sortAssetsByArea>[1] & {
	wax: number;
	paint: number;
	punch: number;
};

type LineInfo = Pick<
	BreakLine,
	| "AssetId"
	| "AssetName"
	| "ParentAssetId"
	| "ParentAssetName"
	| "DefaultShiftHours"
> & {
	/** Paint / Punch / Wax are areas themselves: top-level nodes, no area above them */
	IsSelfArea: boolean;
	path: string[];
};

export type RuleRow = BreakRule & LineInfo & { kind: "rule" };
export type AddRow = LineInfo & {
	kind: "add";
	ShiftHours: ShiftHours;
	HasRules: boolean;
	DefaultHasRules: boolean;
	BreakRuleId: null;
};
export type BreakGridRow = RuleRow | AddRow;

export const SHIFT_LENGTHS: ShiftHours[] = [8, 12];

export const getGridRowId = (row: BreakGridRow) =>
	row.kind === "rule"
		? `rule:${row.BreakRuleId}`
		: `add:${row.AssetId}:${row.ShiftHours}`;

export const buildGridRows = (
	lines: BreakLine[],
	rules: BreakRule[],
	filters: Pick<BreakScheduleFiltersState, "assetId" | "shiftHours">,
	areaIds: AreaIds,
): BreakGridRow[] => {
	const rows: BreakGridRow[] = [];
	const selfAreas = [areaIds.wax, areaIds.paint, areaIds.punch];
	for (const line of sortAssetsByArea(lines, areaIds)) {
		if (filters.assetId != null && line.AssetId !== filters.assetId) continue;
		const isSelfArea = selfAreas.includes(line.AssetId);
		const groupPath = isSelfArea
			? [line.AssetName]
			: [line.ParentAssetName ?? "Other", line.AssetName];
		const info = {
			AssetId: line.AssetId,
			AssetName: line.AssetName,
			ParentAssetId: line.ParentAssetId,
			ParentAssetName: line.ParentAssetName,
			DefaultShiftHours: line.DefaultShiftHours,
			IsSelfArea: isSelfArea,
		};
		const ofLine = rules.filter((r) => r.AssetId === line.AssetId);
		const defaultHasRules = ofLine.some(
			(r) => r.ShiftHours === line.DefaultShiftHours,
		);
		for (const hours of SHIFT_LENGTHS) {
			if (filters.shiftHours != null && hours !== filters.shiftHours) continue;
			const shiftPath = [...groupPath, `${hours}h`];
			const ofScheme = ofLine
				.filter((r) => r.ShiftHours === hours)
				.sort((a, b) => (a.OffsetMinutes ?? 0) - (b.OffsetMinutes ?? 0));
			for (const r of ofScheme) {
				const row: RuleRow = { ...r, ...info, kind: "rule", path: [] };
				row.path = [...shiftPath, getGridRowId(row)];
				rows.push(row);
			}
			const add: AddRow = {
				...info,
				kind: "add",
				ShiftHours: hours,
				HasRules: ofScheme.length > 0,
				DefaultHasRules: defaultHasRules,
				BreakRuleId: null,
				path: [],
			};
			add.path = [...shiftPath, getGridRowId(add)];
			rows.push(add);
		}
	}
	return rows;
};

export type GroupKind = "area" | "line" | "shift";

export const groupKind = (node: IRowNode<BreakGridRow>): GroupKind => {
	const first = node.allLeafChildren?.[0]?.data;
	const depth = node.level + (first?.IsSelfArea ? 1 : 0);
	return depth === 0 ? "area" : depth === 1 ? "line" : "shift";
};
